import { useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { DocumentText } from '@/components/icons';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useModalContext } from '@/components/ui/modal-provider';
import ProceduresTemplatesModal from '@/components/wizard/ProceduresTemplatesModal';
import SidebarActionCard from '@/components/wizard/SidebarActionCard';
import { replaceSessionsInContent } from '@/lib/text';
import { WizardStepProps } from './types';

export default function ProceduresStep({ data, onUpdate }: WizardStepProps) {
    const { openWizardModal, closeModal } = useModalContext();
    const prevSessionsRef = useRef(data.num_sessions);

    useEffect(() => {
        const prev = prevSessionsRef.current;
        const current = data.num_sessions;
        prevSessionsRef.current = current;

        // Only act when num_sessions becomes a valid number different from before
        if (!current || current === prev) return;

        const content = data.procedures?.content;
        if (!content) return;

        const updated = replaceSessionsInContent(content, current);
        if (updated) {
            onUpdate({
                procedures: {
                    ...data.procedures,
                    content: updated,
                },
            });
        }
    }, [data.num_sessions]);

    const handleOpenTemplates = () => {
        openWizardModal({
            title: null,
            size: 'xl',
            content: (
                <ProceduresTemplatesModal
                    existingContent={data.procedures?.content}
                    sessionsCount={data.num_sessions}
                    onCancel={closeModal}
                    onSelect={(text: string) => {
                        onUpdate({
                            procedures: {
                                ...data.procedures,
                                section_title: data.procedures?.section_title || 'Procedimentos',
                                content: text,
                            },
                        });
                        closeModal();
                        toast.success('Texto inserido com sucesso');
                    }}
                />
            ),
        });
    };

    return (
        <div className="flex h-full flex-col">
            <div className="mb-6">
                <Label htmlFor="num_sessions">Quantidade de sessões realizadas</Label>
                <Input
                    id="num_sessions"
                    type="number"
                    min={1}
                    value={data.num_sessions || ''}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        onUpdate({
                            num_sessions: parseInt(e.target.value) || undefined,
                        })
                    }
                    placeholder="Ex: 8"
                    className="mt-1.5"
                />
            </div>

            <div className="mb-6">
                <SidebarActionCard
                    onClick={handleOpenTemplates}
                    title="Modelos prontos"
                    description="Escolha entre modelos para preencher a seção de procedimentos."
                    icon={<DocumentText className="size-5 text-muted-foreground" />}
                />
            </div>
        </div>
    );
}
